import {
  Button,
  Form,
  H5,
  InputPercentage,
  InputCurrency,
} from "@sellar-io/components";
import {
  calculateItemTotal,
  calculatePriceTierDiscount,
  calculateTotal,
  formatCurrency,
  getDiscountDescription,
} from "@sellar-io/common";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../../components/modal/Dialog";
import {
  RadioGroup,
  RadioGroupLabel,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../../../components/radioSelector/RadioGroup";
import {
  CustomDiscountType,
  useOrderOnBehalfOfContext,
} from "../OrderOnBehalfOf.shared";
import { Control, useForm, useWatch } from "react-hook-form";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
} from "../../../../hooks/usePriceFormat";
import { useEffect } from "react";
import { calculateCustomDiscountAmount } from "../helpers/calculateDiscountAmount";
import { useCaseSizeRuleList } from "../../../../hooks/api/v2/caseSizeRule/useCaseSizeRuleList";

type CustomDiscountFormType = {
  type: "percentage" | "flat";
  amount: number;
};

const useCalculateCustomDiscountAmountApi = ({
  orderItems,
  control,
}: {
  orderItems: { price: number; quantity: number }[];
  control: Control<CustomDiscountFormType>;
}) => {
  const { amount: amountInput, type } = useWatch({
    control,
  });

  const itemsTotal = calculateItemTotal(orderItems);

  if (!amountInput || Number.isNaN(amountInput) || type === undefined) {
    return 0;
  }

  const discountAmountApi =
    type === "flat" ? formatPriceInputToApi(amountInput) : amountInput;

  return calculateCustomDiscountAmount({
    totalValue: itemsTotal,
    customDiscount: { amount: discountAmountApi ?? 0, type },
  });
};

export const CustomOrderDiscountDialog = ({
  active,
  onDismiss,
  onSubmit,
  onDelete,
}: {
  active: boolean;
  onDismiss: () => void;
  onSubmit?: (discount: CustomDiscountType) => void;
  onDelete?: () => void;
}) => {
  const {
    customDiscount,
    priceTierDiscountList,
    orderItems,
    customDeliveryCharge,
    calculatedDeliveryCharge,
    connection,
  } = useOrderOnBehalfOfContext();

  const { reset, ...form } = useForm<CustomDiscountFormType>({
    shouldUseNativeValidation: true,
    defaultValues: {
      type: "flat",
      amount: 0,
    },
  });

  const type = useWatch({
    control: form.control,
    name: "type",
  });

  useEffect(() => {
    // populate form with existing custom discount data
    if (customDiscount) {
      reset({
        ...customDiscount,
        amount:
          customDiscount.type === "flat"
            ? parseFloat(formatPriceApiToInput(customDiscount.amount))
            : customDiscount.amount,
      });
    }
  }, [customDiscount, reset, active]);

  const customDiscountAmount = useCalculateCustomDiscountAmountApi({
    orderItems,
    control: form.control,
  });

  const caseSizeRuleListQuery = useCaseSizeRuleList({
    sortBy: "createdAt",
    sortDir: "desc",
  });

  const orderTotalAfterCustomDiscount = calculateTotal({
    orderItems,
    deliveryCharge: customDeliveryCharge ?? calculatedDeliveryCharge,
    discountAmount: customDiscountAmount,
    vatPercentage: connection.Supplier.vatPercentage,
  });

  if (!connection) throw new Error("Connection not defined.");

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={() => onDismiss()}
        title={"Add custom discount"}
      />
      <DialogContent>
        <Form
          className={"flex flex-col gap-4"}
          onSubmit={form.handleSubmit((data) => {
            const amount =
              data.type === "percentage"
                ? data.amount
                : (formatPriceInputToApi(data.amount) ?? 0);

            onSubmit?.({
              type: data.type,
              amount,
            });
          })}
        >
          <RadioGroup
            value={type}
            onChange={(value) => {
              form.setValue("type", value as CustomDiscountFormType["type"]);
            }}
          >
            <RadioGroupLabel className={"sr-only"}>
              Fulfillment Type
            </RadioGroupLabel>
            <RadioGroupContainer>
              <RadioGroupOption value={"flat"}>
                <H5>Fixed amount (£)</H5>
              </RadioGroupOption>
              <RadioGroupOption value={"percentage"}>
                <H5>Percentage (%)</H5>
              </RadioGroupOption>
            </RadioGroupContainer>
          </RadioGroup>
          <div
            className={
              "flex items-center justify-between rounded-lg border border-solid px-4 py-2"
            }
          >
            {type === "percentage" ? (
              <>
                <InputLabel>Discount percentage</InputLabel>
                <InputContainer>
                  <InputPercentage
                    placeholder={"15%"}
                    step={"0.1"}
                    {...form.register("amount", {
                      min: 1,
                      max: 100,
                      required: true,
                      valueAsNumber: true,
                    })}
                  />
                  <span>%</span>
                </InputContainer>
              </>
            ) : (
              <>
                <InputLabel>Discount value</InputLabel>
                <InputContainer>
                  <InputCurrency
                    showPrefix
                    allowNegative
                    min={0}
                    placeholder={"10"}
                    {...form.register("amount", {
                      required: true,
                      valueAsNumber: true,
                    })}
                  />
                </InputContainer>
              </>
            )}
          </div>
          {priceTierDiscountList && (
            <div>
              {priceTierDiscountList.map((discount) => {
                const discountAmount = calculatePriceTierDiscount({
                  caseSizeRuleList: caseSizeRuleListQuery.data?.pages[0] ?? [],
                  orderItems: orderItems.map((item) => ({
                    quantity: item.quantity,
                    unit: item.Product.unit,
                    containerType: item.Product.containerType,
                    price: item.price,
                    discountable: item.discountable,
                    packQuantity: item.Product.packQuantity,
                    volume: item.Product.volume,
                  })),
                  priceTierDiscount: discount,
                });
                return (
                  <div key={discount.itemType}>
                    <div
                      className={
                        "flex justify-between px-4 text-gray-300 line-through"
                      }
                    >
                      <span>
                        {getDiscountDescription({ discount: discount })}
                      </span>
                      <span>{formatCurrency(-discountAmount)}</span>
                    </div>
                  </div>
                );
              })}

              <div className={"flex justify-between px-4"}>
                <span>Order total after custom discount (inc. VAT)</span>
                <span>{formatCurrency(orderTotalAfterCustomDiscount)}</span>
              </div>
            </div>
          )}
          <div className={"flex gap-2 *:flex-1"}>
            {customDiscount && (
              <Button color={"red"} onClick={() => onDelete?.()}>
                Remove
              </Button>
            )}
            <Button
              type={"submit"}
              disabled={!!customDiscount?.amount && customDiscount.amount < 0}
            >
              {customDiscount ? "Update" : "Add discount"}
            </Button>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
